import { Injectable } from '@angular/core';
import { cloneDeep, isNumber } from 'lodash';

import { PatientService } from '@services/patient';
import { tap } from 'rxjs';
import { AuthService } from '../security';
import { ConversionService } from '../biologic-data';

@Injectable({ providedIn: 'root' })
export class FormatFormService {
    currentUser;
    constructor(private authService: AuthService, private conversionService: ConversionService, private patientService: PatientService) {
        this.initUser();
    }

    formatCardio(p) {
        const patient = cloneDeep(p);
        delete patient.logs;
        patient.toComplete = false;

        if (patient.medicalData.nyha === '-') {
            patient.medicalData.nyha = undefined;
        }

        if (patient.inclusion.type !== 'commonLaw') {
            patient.inclusion.modalTemplate = false;
        }

        this.formatPS(patient);
        return patient;
    }

    formatPatientCardio(p, isEdition = false) {
        const patient = this.formatCardio(p);
        return this.createFormData(patient, isEdition);
    }

    formatPatientFast(p) {
        const patient = cloneDeep(p);
        patient.toComplete = true;
        this.formatPS(patient);
        return this.createFormData(patient);
    }

    formatNephro(p) {
        p.treatmentImmunosuppressant = p.treatmentImmunosuppressant?.filter(
            ({ name, value, frequency, borne }) => name || isNumber(value) || isNumber(frequency) || isNumber(borne.min) || isNumber(borne.max),
        );

        const patient = cloneDeep(p);
        delete patient.logs;
        this.formatPS(patient);
        return patient;
    }

    formatJourney(p) {
        const patient = cloneDeep(p);
        delete patient.logs;
        this.formatPS(patient);
        return patient;
    }

    formatPatientNephro(p, isEdition = false) {
        const patient = this.formatNephro(p);
        return this.createFormData(patient, isEdition);
    }

    formatPatientJourney(p, isEdition = false) {
        const patient = this.formatJourney(p);
        return this.createFormData(patient, isEdition);
    }

    formatPS(patient) {
        patient.specialistId = patient.specialistId?._id;
    }

    createFormData(patient, isEdition = false) {
        const formData: FormData = new FormData();

        if (patient.prescription) {
            formData.append('prescription', patient.prescription, patient.prescription.name);
        }

        if (patient.amc) {
            formData.append('amc', patient.amc, patient.amc.name);
        }

        if (!patient.mobileApp || isEdition) {
            formData.append('reasonsNoMobileApp', JSON.stringify(patient.reasonsNoMobileApp));
            formData.append('type', patient.type);
            delete patient.reasonsNoMobileApp;
            delete patient.type;
        }

        formData.append('patient', JSON.stringify(patient));

        return formData;
    }

    initPatient(patient, disease?) {
        const p = {
            ...patient,
            absenceContact: patient.absenceContact || {},
            address: patient.address || {},
            rangGemellaire: patient.rangGemellaire || 1,
            medicalData: patient.medicalData || {},
            cvDatas: patient.cvDatas || {},
            treatment: patient.treatment || {},
        };

        p.phones.phoneToUseToInstall = p.phones?.phoneToUseToInstall ?? 'patient';

        if (!p.kit) {
            p.kit = {
                formerTeleMonitoring: {},
                tablet: {},
                mobile: {
                    hasMobile: true,
                },
                scaleType: 'hs2s',
                addressAndRecipientEdition: {},
                noPreparationRequest: false,
            };
        }

        if (disease === 'nephro') {
            this.initPatientNephro(p);
        } else if (disease === 'cardio') {
            this.initPatientCardio(p);
        }

        return p;
    }

    initPatientNephro(patient) {
        this.conversionService.convertMedicalData(patient.medicalData);

        if (!['etape1', 'commonLaw', 'no', 'toComplete'].includes(patient.inclusion.type)) {
            patient.oldType = patient.inclusion.type;
        }

        patient.medicalData.mainDiagnosis = patient.medicalData.mainDiagnosis || {};
        patient.medicalData.significantEvent = patient.medicalData.significantEvent || {};
        patient.treatmentImmunosuppressant = patient.treatmentImmunosuppressant.map(({ value, frequency, borne, ...treatmentImmunosuppressant }) => ({
            ...treatmentImmunosuppressant,
            value: Number(value),
            frequency: Number(frequency),
            borne: {
                min: Number(borne?.min),
                max: Number(borne?.max),
            },
        }));

        patient.medicalData.racUnit = patient.medicalData.racUnit || this.currentUser.biologicDataUnit.rac;

    }

    initPatientCardio(patient) {
        if (!patient.medicalData.nyha) {
            patient.medicalData.nyha = '-';
        }

        if (patient.inclusion.type === 'toComplete') {
            patient.inclusion.type = 'commonLaw';
        }

        if (patient.inclusion.modalTemplate) {
            patient.inclusion.modalTemplate = !!this.patientService.checkPrescriptionTemplate(patient.specialistId, patient.companyId);
        }

        if (!['etape4', 'commonLaw', 'no', 'inami'].includes(patient.inclusion.type)) {
            patient.oldType = patient.inclusion.type;
        }
        if (patient.inclusion.enableScale === undefined) {
            patient.inclusion.enableScale = true;
        }
        if (patient.inclusion.enableTensio === undefined) {
            patient.inclusion.enableTensio = true;
        }
    }

    private initUser(): void {
        this.authService.currentUser.pipe(tap(user => (this.currentUser = user))).subscribe();
    }
}
